import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/admin/AdminLayout';

interface Template {
  id: number;
  name: string;
  name_ar?: string;
  description?: string;
  description_ar?: string;
  version: number;
  active: boolean;
  steps: TemplateStep[];
}

interface TemplateStep {
  id: number;
  step_number: number;
  title: string;
  title_ar?: string;
  description?: string;
  description_ar?: string;
  fields: TemplateField[];
}

interface TemplateField {
  id: number;
  field_key: string;
  field_type: string;
  label: string;
  label_ar?: string;
  placeholder?: string;
  placeholder_ar?: string;
  is_required: boolean;
  validation_rules?: any;
  options?: FieldOption[];
}

interface FieldOption {
  id: number;
  option_value: string;
  option_label: string;
  option_label_ar?: string;
}

const TemplatePreview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [template, setTemplate] = useState<Template | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [language, setLanguage] = useState<'en' | 'ar'>('en');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (id) {
      fetchTemplatePreview();
    }
  }, [id]);

  const fetchTemplatePreview = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/backend/api/admin/templates/preview.php?id=${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (data.success && data.preview) {
        // Preview API format
        setTemplate(data.preview);
      } else if (data && (data.id || data.name)) {
        // Direct template object response
        setTemplate(data);
      } else if (data.template) {
        setTemplate(data.template);
      } else if (data.success && data.template) {
        // Fallback for success wrapper format
        setTemplate(data.template);
      } else {        setTemplate(null);
      }
    } catch (error) {    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldKey: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [fieldKey]: value
    }));

    // Clear validation error for this field
    if (validationErrors[fieldKey]) {
      setValidationErrors(prev => ({
        ...prev,
        [fieldKey]: ''
      }));
    }
  };

  const validateStep = (stepIndex: number): boolean => {
    if (!template) return false;

    const step = template.steps[stepIndex];
    const errors: Record<string, string> = {};
    let isValid = true;

    step.fields.forEach(field => {
      if (field.is_required && (!formData[field.field_key] || formData[field.field_key] === '')) {
        errors[field.field_key] = language === 'ar' ? 'هذا الحقل مطلوب' : 'This field is required';
        isValid = false;
      }

      // Additional validation based on field type
      if (formData[field.field_key]) {
        switch (field.field_type) {
          case 'email':
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData[field.field_key])) {
              errors[field.field_key] = language === 'ar' ? 'البريد الإلكتروني غير صحيح' : 'Invalid email format';
              isValid = false;
            }
            break;
          case 'phone':
            const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
            if (!phoneRegex.test(formData[field.field_key].replace(/\s/g, ''))) {
              errors[field.field_key] = language === 'ar' ? 'رقم الهاتف غير صحيح' : 'Invalid phone number';
              isValid = false;
            }
            break;
          case 'url':
            try {
              new URL(formData[field.field_key]);
            } catch {
              errors[field.field_key] = language === 'ar' ? 'الرابط غير صحيح' : 'Invalid URL format';
              isValid = false;
            }
            break;
        }
      }
    });

    setValidationErrors(errors);
    return isValid;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, (template?.steps.length || 1) - 1));
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const renderField = (field: TemplateField) => {
    const label = language === 'ar' && field.label_ar ? field.label_ar : field.label;
    const placeholder = language === 'ar' && field.placeholder_ar ? field.placeholder_ar : field.placeholder;
    const fieldValue = formData[field.field_key] || '';
    const hasError = validationErrors[field.field_key];

    const baseInputClasses = `w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
      hasError ? 'border-red-300' : 'border-gray-300'
    } ${language === 'ar' ? 'text-right' : ''}`;

    switch (field.field_type) {
      case 'text':
      case 'email':
      case 'phone':
      case 'url':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type={field.field_type === 'email' ? 'email' : field.field_type === 'phone' ? 'tel' : 'text'}
              value={fieldValue}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={placeholder}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              className={baseInputClasses}
            />
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <textarea
              value={fieldValue}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={placeholder}
              rows={4}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
              className={baseInputClasses}
            />
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="number"
              value={fieldValue}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              placeholder={placeholder}
              className={baseInputClasses}
            />
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="date"
              value={fieldValue}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              className={baseInputClasses}
            />
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'dropdown':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <select
              value={fieldValue}
              onChange={(e) => handleInputChange(field.field_key, e.target.value)}
              className={baseInputClasses}
              dir={language === 'ar' ? 'rtl' : 'ltr'}
            >
              <option value="">
                {language === 'ar' ? 'اختر خياراً' : 'Select an option'}
              </option>
              {field.options?.map((option) => (
                <option key={option.id} value={option.option_value}>
                  {language === 'ar' && option.option_label_ar ? option.option_label_ar : option.option_label}
                </option>
              ))}
            </select>
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'radio':
        return (
          <div key={field.id} className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <div className="space-y-2">
              {field.options?.map((option) => (
                <label key={option.id} className="flex items-center">
                  <input
                    type="radio"
                    name={field.field_key}
                    value={option.option_value}
                    checked={fieldValue === option.option_value}
                    onChange={(e) => handleInputChange(field.field_key, e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">
                    {language === 'ar' && option.option_label_ar ? option.option_label_ar : option.option_label}
                  </span>
                </label>
              ))}
            </div>
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="space-y-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={fieldValue === true}
                onChange={(e) => handleInputChange(field.field_key, e.target.checked)}
                className="mr-2"
              />
              <span className="text-sm text-gray-700">
                {label}
                {field.is_required && <span className="text-red-500 ml-1">*</span>}
              </span>
            </label>
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      case 'file':
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              {label}
              {field.is_required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input
              type="file"
              onChange={(e) => handleInputChange(field.field_key, e.target.files?.[0] || null)}
              className={baseInputClasses}
            />
            {hasError && (
              <p className="text-sm text-red-600">{validationErrors[field.field_key]}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (!template) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Template not found</p>
          <button
            onClick={() => navigate('/admin/packages?tab=templates')}
            className="mt-4 text-primary-600 hover:text-primary-800"
          >
            Back to Templates
          </button>
        </div>
      </AdminLayout>
    );
  }

  const currentStepData = template.steps[currentStep];

  return (
    <AdminLayout>
      <div className="max-w-4xl mx-auto" dir={language === 'ar' ? 'rtl' : 'ltr'}>
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <button
                onClick={() => navigate('/admin/packages?tab=templates')}
                className="text-gray-600 hover:text-gray-900 mb-2"
              >
                ← Back to Templates
              </button>
              <h1 className="text-2xl font-bold text-gray-900">
                Template Preview: {language === 'ar' && template.name_ar ? template.name_ar : template.name}
              </h1>
              <p className="text-gray-600 mt-1">
                {language === 'ar' && template.description_ar ? template.description_ar : template.description}
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">Language:</label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value as 'en' | 'ar')}
                  className="border border-gray-300 rounded-md px-3 py-1 text-sm"
                >
                  <option value="en">English</option>
                  <option value="ar">العربية</option>
                </select>
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / template.steps.length) * 100}%` }}
            ></div>
          </div>

          <div className="flex justify-between text-sm text-gray-600">
            <span>
              {language === 'ar' ? 'الخطوة' : 'Step'} {currentStep + 1} {language === 'ar' ? 'من' : 'of'} {template.steps.length}
            </span>
            <span>
              {Math.round(((currentStep + 1) / template.steps.length) * 100)}% {language === 'ar' ? 'مكتمل' : 'Complete'}
            </span>
          </div>
        </div>

        {/* Step Content */}
        {currentStepData && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                {language === 'ar' && currentStepData.title_ar ? currentStepData.title_ar : currentStepData.title}
              </h2>
              {currentStepData.description && (
                <p className="text-gray-600">
                  {language === 'ar' && currentStepData.description_ar ? currentStepData.description_ar : currentStepData.description}
                </p>
              )}
            </div>

            {/* Fields */}
            <div className="space-y-6">
              {currentStepData.fields.map(field => renderField(field))}
            </div>

            {currentStepData.fields.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                {language === 'ar' ? 'لا توجد حقول في هذه الخطوة' : 'No fields in this step'}
              </div>
            )}
          </div>
        )}

        {/* Navigation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between">
            <button
              onClick={prevStep}
              disabled={currentStep === 0}
              className={`px-6 py-2 rounded-md font-medium transition-colors ${
                currentStep === 0
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {language === 'ar' ? 'السابق' : 'Previous'}
            </button>

            <div className="flex space-x-2">
              {template.steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-8 h-8 rounded-full font-medium text-sm transition-colors ${
                    index === currentStep
                      ? 'bg-primary-600 text-white'
                      : index < currentStep
                      ? 'bg-green-100 text-green-600'
                      : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>

            {currentStep === template.steps.length - 1 ? (
              <button
                onClick={() => {
                  if (validateStep(currentStep)) {
                    alert(language === 'ar' ? 'تم إرسال النموذج بنجاح!' : 'Form submitted successfully!');
                  }
                }}
                className="px-6 py-2 bg-green-600 text-white rounded-md font-medium hover:bg-green-700 transition-colors"
              >
                {language === 'ar' ? 'إرسال' : 'Submit'}
              </button>
            ) : (
              <button
                onClick={nextStep}
                className="px-6 py-2 bg-primary-600 text-white rounded-md font-medium hover:bg-primary-700 transition-colors"
              >
                {language === 'ar' ? 'التالي' : 'Next'}
              </button>
            )}
          </div>
        </div>

        {/* Debug Info (Admin Only) */}
        <div className="mt-6 bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Debug Information</h3>
          <div className="text-xs text-gray-600 space-y-1">
            <p><strong>Template ID:</strong> {template.id}</p>
            <p><strong>Version:</strong> {template.version}</p>
            <p><strong>Total Steps:</strong> {template.steps.length}</p>
            <p><strong>Current Step Fields:</strong> {currentStepData?.fields.length || 0}</p>
            <p><strong>Form Data:</strong> {JSON.stringify(formData, null, 2)}</p>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default TemplatePreview;